import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Loader2, Check, X } from 'lucide-react';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/auth/hooks/useAuth';
import { cn } from '@/lib/utils';

interface FindingRow {
  severity?: string;
  category: string;
  description?: string;
  file?: string;
  line?: number;
  evidence?: string;
  suggestion?: string;
  package?: string;
  version?: string;
}

const SEVERITY_BG: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-300',
  info: 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-300',
};

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  },
  reviewing: {
    label: 'AI reviewing',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  human_review: {
    label: 'Awaiting human reviewer',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  building: {
    label: 'Building',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  published: {
    label: 'Published',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? {
    label: status,
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center px-3 py-1 rounded text-sm font-medium',
        style.className,
      )}
    >
      {style.label}
    </span>
  );
}

function FindingsList({ title, findings }: { title: string; findings: FindingRow[] }) {
  if (findings.length === 0) return null;
  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-3">
        {title} <span className="text-muted-foreground font-normal">({findings.length})</span>
      </h3>
      <ul className="space-y-3">
        {findings.map((f, i) => {
          const sev = f.severity ?? 'info';
          const where = f.file
            ? `${f.file}${f.line ? `:${f.line}` : ''}`
            : f.package
              ? `${f.package}${f.version ? `@${f.version}` : ''}`
              : f.category;
          return (
            <li key={i} className="text-sm">
              <div className="flex items-start gap-2 flex-wrap">
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase',
                    SEVERITY_BG[sev] ?? SEVERITY_BG.info,
                  )}
                >
                  {sev}
                </span>
                <span className="font-mono text-xs text-foreground">{where}</span>
              </div>
              {f.description && <p className="mt-1 ml-1 text-foreground">{f.description}</p>}
              {f.suggestion && <p className="mt-1 ml-1 text-muted-foreground">→ {f.suggestion}</p>}
              {f.evidence && !f.file && (
                <code className="mt-1 ml-1 inline-block text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {f.evidence}
                </code>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function PluginSubmissionDetail() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).includes('admin');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.submissions.detail(submissionId ?? '__none__'),
    queryFn: () => api.getPluginSubmission(submissionId!),
    enabled: !!submissionId,
  });

  const approveMutation = useMutation({
    mutationFn: (reviewId: string) => api.approvePluginReview(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all });
      navigate('/dev-console/admin/reviews');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ reviewId, notes }: { reviewId: string; notes: string }) =>
      api.rejectPluginReview(reviewId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all });
      setRejectModalOpen(false);
      navigate('/dev-console/admin/reviews');
    },
  });

  if (isLoading) return <p className="p-8 text-muted-foreground">Loading...</p>;
  if (error)
    return <p className="p-8 text-destructive">Failed to load: {(error as Error).message}</p>;
  if (!data) return null;

  const { submission, review } = data;
  const archeName = (submission.manifest as { name?: string })?.name ?? submission.archId;

  const codeFindings = (review?.codeFindings ?? []) as FindingRow[];
  const dependencyFindings = (review?.dependencyFindings ?? []) as FindingRow[];
  const policyFindings = (review?.policyFindings ?? []) as FindingRow[];
  const qualityFindings = (review?.qualityFindings ?? []) as FindingRow[];

  const showAdminActions = isAdmin && submission.status === 'human_review' && submission.reviewId;

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-6">
      <div>
        <Link
          to={isAdmin ? '/dev-console/admin/reviews' : '/submissions'}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{archeName}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">{submission.submissionId}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Submitted {new Date(submission.createdAt).toLocaleString()}
          </p>
        </div>
        <StatusBadge status={submission.status} />
      </div>

      {review && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Risk score:</span>{' '}
              <span className="font-semibold">{review.riskScore ?? '—'} / 100</span>
            </div>
            <div>
              <span className="text-muted-foreground">Critical:</span>{' '}
              <span className="font-semibold">{review.criticalCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">High:</span>{' '}
              <span className="font-semibold">{review.highCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Files analyzed:</span>{' '}
              <span className="font-semibold">{review.filesAnalyzed}</span>
            </div>
          </div>
        </Card>
      )}

      {submission.status === 'human_review' && (
        <Card className="p-4 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
          <p className="text-sm">
            A human reviewer is taking a look at this submission — usually under 48 hours. You'll
            receive an email when it's ready.
          </p>
        </Card>
      )}

      {submission.status === 'rejected' && submission.errorMessage && (
        <Card className="p-4 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900">
          <p className="text-sm text-red-900 dark:text-red-300 font-medium mb-2">
            Submission rejected
          </p>
          <p className="text-sm">{submission.errorMessage}</p>
          {submission.archId && !isAdmin && (
            <Link
              to={`/a/${submission.archId}`}
              className="inline-flex items-center gap-1 mt-3 text-sm font-medium hover:underline"
            >
              Open plugin builder to fix
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </Card>
      )}

      {submission.status === 'published' && submission.archId && (
        <Card className="p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
          <p className="text-sm font-medium mb-2">Your arche is live</p>
          <Link
            to={`/a/${submission.archId}`}
            className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
          >
            Open arche
            <ExternalLink className="h-3 w-3" />
          </Link>
        </Card>
      )}

      <FindingsList title="Policy findings" findings={policyFindings} />
      <FindingsList title="Code findings" findings={codeFindings} />
      <FindingsList title="Dependency findings" findings={dependencyFindings} />
      <FindingsList title="Quality findings" findings={qualityFindings} />

      {showAdminActions && (
        <Card className="p-4 border-amber-300 dark:border-amber-700">
          <h3 className="font-semibold mb-3">Admin actions</h3>
          <div className="flex gap-3">
            <Button
              onClick={() => approveMutation.mutate(submission.reviewId!)}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              {approveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Approve & build
            </Button>
            <Button
              variant="destructive"
              onClick={() => setRejectModalOpen(true)}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Reject with notes
            </Button>
          </div>

          {rejectModalOpen && (
            <div className="mt-4 space-y-3">
              <Textarea
                placeholder="Why are you rejecting this submission? The contributor will see this in the rejection email."
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={4}
              />
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  disabled={!rejectNotes.trim() || rejectMutation.isPending}
                  onClick={() =>
                    rejectMutation.mutate({
                      reviewId: submission.reviewId!,
                      notes: rejectNotes.trim(),
                    })
                  }
                >
                  {rejectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Confirm reject
                </Button>
                <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
